import React, { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import Avatar from '../components/Avatar';
import StarRating from '../components/StarRating';

const ChevronLeft = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="15 18 9 12 15 6"/>
  </svg>
);
const ClockIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
  </svg>
);
const SeatIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/>
    <path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>
  </svg>
);

function formatDate(dateStr) {
  const [y, m, d] = dateStr.split('-').map(Number);
  const date = new Date(y, m - 1, d);
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const diff = Math.round((date - today) / 86400000);
  const label = diff === 0 ? ' (Today)' : diff === 1 ? ' (Tomorrow)' : '';
  return date.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' }) + label;
}

function formatTime(t) {
  const [h, m] = t.split(':').map(Number);
  const ampm = h >= 12 ? 'PM' : 'AM';
  return `${h % 12 || 12}:${m.toString().padStart(2, '0')} ${ampm}`;
}

export default function RideDetail() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { getRideById, getUserById, currentUser, requestRide, respondToRequest, cancelRide, conversations } = useApp();
  const [requestSent, setRequestSent] = useState(false);
  const [error, setError] = useState('');

  const ride = getRideById(id);
  const driver = ride ? getUserById(ride.driverId) : null;

  const isDriver = ride?.driverId === currentUser?.id;
  const myRequest = ride?.requests.find(r => r.riderId === currentUser?.id);
  const isAccepted = myRequest?.status === 'accepted';

  const conv = useMemo(() => {
    if (!isAccepted && !isDriver) return null;
    return conversations.find(c =>
      c.rideId === id &&
      c.participants.includes(currentUser.id)
    );
  }, [conversations, id, currentUser, isAccepted, isDriver]);

  if (!ride || !driver) {
    return (
      <div className="page" style={{ textAlign: 'center', paddingTop: 80 }}>
        <p style={{ color: 'var(--gray-400)' }}>Ride not found.</p>
        <button className="btn btn-ghost" onClick={() => navigate(-1)} style={{ marginTop: 16 }}>Go Back</button>
      </div>
    );
  }

  const handleRequest = () => {
    const result = requestRide(id);
    if (result.error) { setError(result.error); return; }
    setRequestSent(true);
    setError('');
  };

  const handleRespond = (riderId, action) => {
    respondToRequest(id, riderId, action);
  };

  const statusColor = { open: 'var(--green)', full: 'var(--yellow)', cancelled: 'var(--red)', completed: 'var(--gray-400)' }[ride.status];

  return (
    <div className="page">
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 24, paddingTop: 8 }}>
        <button onClick={() => navigate(-1)} style={{ background: 'none', padding: 4, color: 'var(--gray-300)', marginRight: 12 }}>
          <ChevronLeft />
        </button>
        <h1 style={{ fontSize: 20, fontWeight: 800, flex: 1 }}>Ride Details</h1>
        <span style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 600, color: statusColor }}>
          <div style={{ width: 8, height: 8, borderRadius: '50%', background: statusColor }} />
          {ride.status.charAt(0).toUpperCase() + ride.status.slice(1)}
        </span>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      {/* Route card */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 16 }}>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', paddingTop: 4 }}>
            <div style={{ width: 10, height: 10, borderRadius: '50%', background: 'var(--green)', flexShrink: 0 }} />
            <div style={{ width: 2, height: 32, background: 'var(--navy-border)', margin: '4px 0' }} />
            <div style={{ width: 10, height: 10, borderRadius: '50%', background: 'var(--accent)', flexShrink: 0 }} />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ marginBottom: 20 }}>
              <p style={{ fontSize: 11, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 4 }}>From</p>
              <p style={{ fontSize: 16, fontWeight: 600 }}>{ride.departure}</p>
            </div>
            <div>
              <p style={{ fontSize: 11, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 4 }}>To</p>
              <p style={{ fontSize: 16, fontWeight: 600 }}>{ride.destination}</p>
            </div>
          </div>
        </div>

        <div className="divider" style={{ margin: '12px 0' }} />

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div>
            <p style={{ fontSize: 11, color: 'var(--gray-500)', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Date & Time</p>
            <p style={{ fontSize: 13, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 5 }}>
              <ClockIcon /> {formatDate(ride.date)}
            </p>
            <p style={{ fontSize: 13, color: 'var(--gray-300)', marginTop: 2, paddingLeft: 20 }}>{formatTime(ride.time)}</p>
          </div>
          <div>
            <p style={{ fontSize: 11, color: 'var(--gray-500)', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Seats & Price</p>
            <p style={{ fontSize: 13, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 5 }}>
              <SeatIcon /> {ride.seatsAvailable} of {ride.seats} left
            </p>
            <p style={{ fontSize: 15, fontWeight: 800, color: 'var(--accent)', marginTop: 2, paddingLeft: 20 }}>
              ${ride.price}/person
            </p>
          </div>
        </div>
      </div>

      {/* Driver card */}
      <div className="card" style={{ marginBottom: 20 }}>
        <p style={{ fontSize: 11, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 12 }}>Driver</p>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button onClick={() => navigate(`/profile/${driver.id}`)} style={{ background: 'none', padding: 0 }}>
            <Avatar user={driver} size={52} />
          </button>
          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
              <button onClick={() => navigate(`/profile/${driver.id}`)} style={{ background: 'none', padding: 0 }}>
                <span style={{ fontSize: 16, fontWeight: 700 }}>{driver.name}</span>
              </button>
              {driver.isVerified && <span className="badge badge-verified" style={{ fontSize: 10 }}>✓ Verified</span>}
            </div>
            <p style={{ fontSize: 13, color: 'var(--gray-400)', marginBottom: 4 }}>{driver.university}</p>
            <StarRating rating={driver.rating} count={driver.ratingCount} size={13} />
          </div>
        </div>
        {driver.car && (
          <div style={{
            marginTop: 12, paddingTop: 12, borderTop: '1px solid var(--navy-border)',
            fontSize: 13, color: 'var(--gray-300)',
          }}>
            🚗 {driver.car.color} {driver.car.make} {driver.car.model}
          </div>
        )}
      </div>

      {/* Action area */}
      {!isDriver && (
        <div>
          {isAccepted ? (
            <div>
              <div className="alert alert-success" style={{ marginBottom: 12 }}>
                ✅ You're on this ride!
              </div>
              {conv && (
                <button className="btn btn-primary btn-full" onClick={() => navigate(`/messages/${conv.id}`)}>
                  💬 Message Driver
                </button>
              )}
            </div>
          ) : myRequest?.status === 'pending' || requestSent ? (
            <div className="alert alert-info">
              ⏳ Your request is pending. The driver will respond soon.
            </div>
          ) : myRequest?.status === 'declined' ? (
            <div className="alert alert-error">
              ❌ Your request was declined for this ride.
            </div>
          ) : ride.status === 'open' ? (
            <button className="btn btn-primary btn-full" onClick={handleRequest}
              style={{ padding: '14px', fontSize: 15 }}>
              Request to Join
            </button>
          ) : (
            <div style={{
              background: 'var(--navy-light)', border: '1px solid var(--navy-border)',
              borderRadius: 'var(--radius-sm)', padding: '14px 16px', textAlign: 'center',
              color: 'var(--gray-400)', fontSize: 14,
            }}>
              This ride is {ride.status}.
            </div>
          )}
        </div>
      )}

      {/* Driver management */}
      {isDriver && (
        <div>
          <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 14 }}>
            Requests ({ride.requests.length})
          </h3>

          {ride.requests.length === 0 ? (
            <div className="card" style={{ textAlign: 'center', color: 'var(--gray-400)', fontSize: 14 }}>
              No requests yet.
            </div>
          ) : (
            ride.requests.map(req => {
              const rider = getUserById(req.riderId);
              if (!rider) return null;
              const riderConv = conversations.find(c =>
                c.rideId === id && c.participants.includes(req.riderId)
              );
              return (
                <div key={req.riderId} style={{
                  background: 'var(--navy-light)', border: '1px solid var(--navy-border)',
                  borderRadius: 'var(--radius)', padding: 16, marginBottom: 12,
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
                    <button onClick={() => navigate(`/profile/${rider.id}`)} style={{ background: 'none', padding: 0 }}>
                      <Avatar user={rider} size={40} />
                    </button>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 15, fontWeight: 600 }}>{rider.name}</div>
                      <div style={{ fontSize: 12, color: 'var(--gray-400)' }}>{rider.university}</div>
                      <StarRating rating={rider.rating} count={rider.ratingCount} size={12} />
                    </div>
                    <span className={`badge badge-${req.status}`}>
                      {req.status.charAt(0).toUpperCase() + req.status.slice(1)}
                    </span>
                  </div>

                  {req.status === 'pending' && (
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button className="btn btn-success" style={{ flex: 1 }}
                        onClick={() => handleRespond(req.riderId, 'accepted')}>
                        Accept
                      </button>
                      <button className="btn btn-danger" style={{ flex: 1 }}
                        onClick={() => handleRespond(req.riderId, 'declined')}>
                        Decline
                      </button>
                    </div>
                  )}
                  {req.status === 'accepted' && riderConv && (
                    <button className="btn btn-ghost btn-full"
                      onClick={() => navigate(`/messages/${riderConv.id}`)}>
                      💬 Chat with {rider.name.split(' ')[0]}
                    </button>
                  )}
                </div>
              );
            })
          )}

          <div className="divider" />
          {ride.status !== 'cancelled' && (
            <button className="btn btn-danger btn-full"
              onClick={() => { cancelRide(id); navigate('/my-rides'); }}>
              Cancel This Ride
            </button>
          )}
        </div>
      )}
    </div>
  );
}
